/**
 * Checks whether a click target, or any of its ancestors up to the row boundary, is an
 * interactive element. An element is considered interactive if it is one of the interactive
 * HTML tags (`A`, `BUTTON`, `LABEL`, `INPUT`, `SELECT`) or has a non-negative `tabIndex`
 * attribute. Elements with `tabIndex="-1"` are intentionally excluded — they are focusable
 * programmatically but are not interactive controls.
 * @param row - The row element that acts as the boundary for the ancestor walk.
 * @param target - The element that received the interaction.
 * @returns `true` if an interactive element was found between the target and the row, `false` otherwise.
 */
export function isTargetInteractive(
  row: Element,
  target: HTMLElement | SVGElement,
): boolean {
  if (row === target) {
    return false;
  }

  const interactiveTags = new Set(['A', 'BUTTON', 'LABEL', 'INPUT', 'SELECT']);

  let currentElement: HTMLElement | SVGElement | null = target;
  do {
    const tabIndex = currentElement?.getAttribute('tabindex');
    const isInTabOrder = Number(tabIndex ?? -1) >= 0;
    const hitInteractiveElement =
      isInTabOrder || interactiveTags.has(currentElement?.tagName);

    if (hitInteractiveElement) {
      return true;
    }
    currentElement = currentElement?.parentElement;
  } while (currentElement !== row && currentElement !== null);
  return false;
}
